import { useEffect, useMemo, useState } from "react";
import {
  Category,
  Skill,
  SkillCodes,
} from "../../../manager/components/skill-codes/SkillCodes";
import lunr from "lunr";
import {
  hyphenPlugin,
  lunrCustomTrimmerPlugIn,
  lunrNumberPlugIn,
  lunrSearch,
  lunrSpecialCasePlugIn,
  whitelistPlugin,
} from "./lunrUtils";
import { useQuery } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";
import { MiscSearchData } from "../../types";
import { whitelistStopWords } from "../../utils/searchPrototypeData";

type SearchResults = {
  categoryResults: Category[];
  skillResults: Skill[];
};

const initialSearchResults: SearchResults = {
  categoryResults: [],
  skillResults: [],
};

function generateLunrSearchResults(
  query: string,
  categorySearch: lunr.Index,
  skillSearch: lunr.Index,
  categories: Category[],
  skills: Skill[],
  miscSearchData: MiscSearchData
): SearchResults {
  const lunrCatResults = lunrSearch(query, categorySearch, miscSearchData);
  const lunrSkillResults = lunrSearch(query, skillSearch, miscSearchData);

  const categoryResults = lunrCatResults.map((result) => {
    return categories.find(
      (category) => category.code === result.ref
    ) as Category;
  });
  const skillResults = lunrSkillResults.map((result) => {
    return skills.find((skill) => skill.__index === result.ref) as Skill;
  });

  console.group(`LUNR Search Results for Query: ${query}`);
  console.group("Category Results");
  console.log(lunrCatResults);
  console.groupEnd();
  console.group("Skill Results");
  console.log(lunrSkillResults);
  console.groupEnd();
  console.groupEnd();

  return {
    categoryResults,
    skillResults,
  };
}

export function useSkillSearchResults(
  query: string,
  data: SkillCodes,
  allCategories: Category[]
): SearchResults {
  const [results, setResults] = useState<SearchResults>(initialSearchResults);

  const { data: miscSearchData } = useQuery<MiscSearchData>({
    queryKey: [`/teacher/search/misc-search-data`],
    queryFn: () =>
      executeQuery({
        path: `/teacher/search/misc-search-data`,
        customerServiceHeader: true,
      }),
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const allSkills = useMemo(
    () =>
      allCategories.flatMap((category) =>
        category.skills.map((skill) => {
          return {
            ...data.skills[skill],
            __index: skill,
          };
        })
      ),
    [allCategories, data.skills]
  );

  const categorySearch = useMemo(() => {
    return lunr(function () {
      this.use(hyphenPlugin);
      this.use(lunrSpecialCasePlugIn);
      this.use(lunrNumberPlugIn);
      this.use(lunrCustomTrimmerPlugIn);
      this.use(whitelistPlugin, whitelistStopWords);
      this.k1(1);
      this.b(0);
      this.ref("code");
      this.field("name");
      allCategories.forEach((category) => {
        this.add({
          code: category.code,
          name: category.name,
        });
      });
    });
  }, [allCategories]);

  const skillSearch = useMemo(() => {
    return lunr(function () {
      this.use(hyphenPlugin);
      this.use(lunrSpecialCasePlugIn);
      this.use(lunrNumberPlugIn);
      this.use(lunrCustomTrimmerPlugIn);
      this.use(whitelistPlugin, whitelistStopWords);
      this.k1(1);
      this.b(0);
      this.ref("__index");
      this.field("name");
      this.field("search_tags");
      allSkills.forEach((skill) => {
        this.add({
          __index: skill.__index,
          name: skill.name,
          search_tags: skill.search_tags?.join(" ") || "",
        });
      });
    });
  }, [allSkills]);

  useEffect(() => {
    if (query.length > 0 && miscSearchData) {
      setResults(
        generateLunrSearchResults(
          query,
          categorySearch,
          skillSearch,
          allCategories,
          allSkills,
          miscSearchData
        )
      );
    } else {
      setResults({
        categoryResults: allCategories,
        skillResults: [],
      });
    }
  }, [
    categorySearch,
    skillSearch,
    query,
    data,
    allCategories,
    allSkills,
    miscSearchData,
  ]);

  return results;
}
